import React, { CSSProperties, forwardRef, useEffect } from 'react';
import type { DraggableSyntheticListeners } from '@dnd-kit/core';
import type { Transform } from '@dnd-kit/utilities';
import { DragHandleDots2Icon, TrashIcon } from '@radix-ui/react-icons';

import { cn } from '@/lib/utils';

export interface ActionProps extends React.HTMLAttributes<HTMLButtonElement> {
  active?: {
    fill: string;
    background: string;
  };
  cursor?: CSSProperties['cursor'];
}

export const Action = forwardRef<HTMLButtonElement, ActionProps>(
  ({ active, className, cursor, style, onClick, ...props }, ref) => {
    return (
      <button
        ref={ref}
        {...props}
        className={cn(
          'flex w-fit flex-none touch-none appearance-none items-center justify-center rounded border-none bg-transparent p-0 px-1 py-1 outline-none',
          className
        )}
        tabIndex={0}
        style={
          {
            ...style,
            cursor,
            '--fill': active?.fill,
            '--background': active?.background,
          } as CSSProperties
        }
        onClick={!confirm ? onClick : undefined}
      />
    );
  }
);

export const Handle = forwardRef<HTMLButtonElement, ActionProps>((props, ref) => {
  return (
    <Action ref={ref} cursor="grab" {...props}>
      <DragHandleDots2Icon className="w-4 shrink-0 text-gray-400" />
    </Action>
  );
});

export function Remove(props: ActionProps) {
  return (
    <Action
      {...props}
      active={{
        fill: 'rgba(255, 70, 70, 0.95)',
        background: 'rgba(255, 70, 70, 0.1)',
      }}
    >
      <TrashIcon className="w-4 h-4 shrink-0" />
    </Action>
  );
}

export interface Props {
  dragOverlay?: boolean;
  color?: string;
  disabled?: boolean;
  dragging?: boolean;
  handle?: boolean;
  handleProps?: any;
  height?: number;
  index?: number;
  fadeIn?: boolean;
  transform?: Transform | null;
  listeners?: DraggableSyntheticListeners;
  sorting?: boolean;
  style?: React.CSSProperties;
  transition?: string | null;
  wrapperStyle?: React.CSSProperties;
  value: React.ReactNode;
  onRemove?(): void;
  //   renderItem?(args: {
  //     dragOverlay: boolean;
  //     dragging: boolean;
  //     sorting: boolean;
  //     index: number | undefined;
  //     fadeIn: boolean;
  //     listeners: DraggableSyntheticListeners;
  //     ref: React.Ref<HTMLElement>;
  //     style: React.CSSProperties | undefined;
  //     transform: Props["transform"];
  //     transition: Props["transition"];
  //     value: Props["value"];
  //   }): React.ReactElement;
}

export const SortableItem = React.memo(
  React.forwardRef<HTMLDivElement, Props>(
    (
      {
        color,
        dragOverlay,
        dragging,
        disabled,
        fadeIn,
        handle,
        handleProps,
        height,
        index,
        listeners,
        onRemove,
        // renderItem,
        sorting,
        style,
        transition,
        transform,
        value,
        wrapperStyle,
        ...props
      },
      ref
    ) => {
      useEffect(() => {
        if (!dragOverlay) {
          return;
        }

        document.body.style.cursor = 'grabbing';

        return () => {
          document.body.style.cursor = '';
        };
      }, [dragOverlay]);

      //   if (renderItem) {
      //     return (
      //         renderItem({
      //           dragOverlay: Boolean(dragOverlay),
      //           dragging: Boolean(dragging),
      //           sorting: Boolean(sorting),
      //           index,
      //           fadeIn: Boolean(fadeIn),
      //           listeners,
      //           ref,
      //           style,
      //           transform,
      //           transition,
      //           value,
      //         })
      //       )

      //   }

      return (
        <div
          // className={cn(
          //   styles.Wrapper,
          // )}
          className={cn('group flex-1 h-fit min-w-0 max-w-full box-border flex transform-gpu touch-manipulation', {
            'z-10': sorting,
            'z-50 scale-105': dragOverlay,
          })}
          style={
            {
              ...wrapperStyle,
              transform: `translate3d(var(--translate-x, 0), var(--translate-y, 0), 0)
    scaleX(var(--scale-x, 1)) scaleY(var(--scale-y, 1))`,
              transition: [transition, wrapperStyle?.transition].filter(Boolean).join(', '),
              '--translate-x': transform ? `${Math.round(transform.x)}px` : undefined,
              '--translate-y': transform ? `${Math.round(transform.y)}px` : undefined,
              '--scale-x': transform?.scaleX ? `${transform.scaleX}` : undefined,
              '--scale-y': transform?.scaleY ? `${transform.scaleY}` : undefined,
              '--index': index,
              '--color': color,
            } as React.CSSProperties
          }
          ref={ref}
        >
          <div
            className={cn(
              'relative box-border flex-grow origin-center list-none items-center whitespace-nowrap rounded border bg-white p-0 font-normal text-gray-800 outline-none transition-shadow duration-200 ease-in-out',
              'flex gap-x-1.5',
              {
                'z-0 opacity-80': dragging,
                'cursor-grab': !handle,
                'cursor-grabbing': dragOverlay,
                'cursor-not-allowed bg-gray-100 text-gray-500': disabled,
                "before:absolute before:left-0 before:top-1/2 before:block before:h-full before:w-1 before:-translate-y-1/2 before:rounded-l-sm before:content-['']":
                  color,
              }
            )}
            style={style}
            {...(!handle ? listeners : undefined)}
            {...props}
            tabIndex={!handle ? 0 : undefined}
          >
            {handle ? <Handle {...handleProps} {...listeners} /> : null}
            {/* <div className="flex-1 cursor-default">{JSON.stringify(value, null, 2)}</div> */}
            {value}
            {/* {value} */}
            {/* <span className="ml-auto mr-1 flex h-full items-center justify-center self-start">
              {onRemove ? <Remove className="invisible group-hover:visible" onClick={onRemove} /> : null}
            </span> */}
          </div>
        </div>
      );
    }
  )
);
